
import { useState } from 'react';

export default function Demo() {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showVideoModal, setShowVideoModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d60rkuoomoleh5thvahg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="demo-section" className="py-16 sm:py-24 bg-gradient-to-br from-[#6D28D9] to-[#8B5CF6] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Prueba PreBedAI Ahora
            </h2>
            <p className="text-base sm:text-xl text-white/90 mb-3 sm:mb-4 px-2">
              Entorno de pruebas completo. Configura tu hospital en minutos.
            </p>
            <p className="text-white/70 text-xs sm:text-sm flex items-center justify-center gap-2">
              <i className="ri-lightbulb-line"></i>
              Entorno de pruebas: no es necesario introducir datos reales
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 sm:gap-8 items-start">
            {/* Left: Form */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-white/20">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Regístrate</h3>
              
              <div className="space-y-4 sm:space-y-5">
                <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                  PreBedAI te ofrece un entorno de pruebas completo para que explores todas las funcionalidades sin compromiso. Accede directamente a la demo y descubre cómo optimizar la gestión de camas en tu hospital.
                </p>
                
                <a
                  href="https://prebedai.mct-origin.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#6D28D9] rounded-lg font-bold text-base sm:text-lg hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-center"
                >
                  <span>Acceder a la Demo</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                  <i className="ri-check-line text-green-300"></i>
                  <span>Acceso inmediato · Sin tarjeta</span>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                  <i className="ri-check-line text-green-300"></i>
                  <span>Datos demo incluidos</span>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                  <i className="ri-check-line text-green-300"></i>
                  <span>Configura tus plantas/camas en minutos</span>
                </div>
              </div>
            </div>

            {/* Right: Video Preview */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-white/20">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">¿Prefieres ver cómo funciona?</h3>
              
              <div className="relative aspect-video bg-black/30 rounded-lg sm:rounded-xl overflow-hidden mb-4 sm:mb-6 group cursor-pointer" onClick={() => setShowVideoModal(true)}>
                <img
                  src="https://img.youtube.com/vi/Va_f0FoVO44/maxresdefault.jpg"
                  alt="Vista previa del tutorial"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-[#6D28D9] text-2xl sm:text-4xl ml-1"></i>
                  </div>
                </div>
              </div>

              <a
                href="https://youtu.be/Va_f0FoVO44"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-white/30 text-sm sm:text-base text-center"
              >
                <i className="ri-play-circle-line text-lg sm:text-xl"></i>
                <span>Ver Tutorial</span>
              </a>

              <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                <p className="text-white/90 text-xs sm:text-sm font-medium">En el tutorial verás:</p>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-start gap-2 text-white/80 text-xs sm:text-sm">
                    <i className="ri-checkbox-circle-line text-[#06B6D4] flex-shrink-0 mt-0.5"></i>
                    <span>Cómo funciona el mapa interactivo de camas</span>
                  </div>
                  <div className="flex items-start gap-2 text-white/80 text-xs sm:text-sm">
                    <i className="ri-checkbox-circle-line text-[#06B6D4] flex-shrink-0 mt-0.5"></i>
                    <span>Predicciones de IA en acción</span>
                  </div>
                  <div className="flex items-start gap-2 text-white/80 text-xs sm:text-sm">
                    <i className="ri-checkbox-circle-line text-[#06B6D4] flex-shrink-0 mt-0.5"></i>
                    <span>Flujo completo de limpieza y asignación</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sandbox Icon with Tooltip */}
          <div className="mt-6 sm:mt-8 flex justify-center">
            <div className="group relative inline-block">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20 cursor-help">
                <i className="ri-flask-line text-white text-lg sm:text-xl"></i>
                <span className="text-white/90 text-xs sm:text-sm font-medium">Entorno Sandbox</span>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 sm:w-64 bg-black/90 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <p className="leading-relaxed">
                  Este es un entorno de pruebas aislado. Puedes experimentar libremente sin afectar datos reales.
                </p>
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-black/90"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative max-w-4xl w-full bg-[#0A1628] rounded-xl sm:rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10 cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl sm:text-2xl"></i>
            </button>
            
            <div className="aspect-video bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/Va_f0FoVO44?autoplay=1"
                title="Tutorial PreBedAI"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
